import React, { useEffect, useState } from 'react';
import ModalDefault from '../../ModalDefault';
import ButtonDefault from '../../form/ButtonDefault';
import { IconAccept, IconClose, IconWrong } from '../../icons';
import { ModalWrapper, ModalTitle, ModalButtons } from '../../../pages/Demanda/styles';
import styled from 'styled-components';

const TextareaContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100% !important;
    max-width: 100%;
    box-sizing: border-box;

    label {
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.interfaceText400};
        line-height: 20px;
        text-align: left;
    }

    textarea {
        width: 100%;
        max-width: 100%;
        min-height: 120px;
        padding: 12px;
        border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
        border-radius: 6px;
        font-size: 14px;
        font-family: inherit;
        resize: vertical;
        transition: border-color 0.2s;
        box-sizing: border-box;
        line-height: 20px;
        text-align: left;

        &:focus {
            outline: none;
            border-color: ${({ theme }) => theme.colors.primaryRed400};
        }

        &::placeholder {
            color: ${({ theme }) => theme.colors.interfaceText200};
        }
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100% !important;
    box-sizing: border-box;
    gap: 0;

    > * {
        width: 100% !important;
        max-width: 100% !important;
    }

    p {
        margin: 0;
        padding: 0;
        text-align: left;
    }
`;

const Opcoes = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
`;

const OpcaoCard = styled.button<{ variante: 'aceitar' | 'recusar'; selecionado: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 14px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
    background: ${({ theme, selecionado }) => selecionado ? theme.colors.interfacePure100 : theme.colors.interfacePure0};
    cursor: pointer;
    text-align: left;
    transition: border-color 0.2s, background 0.2s;

    &:hover {
        border-color: ${({ theme }) => theme.colors.primaryRed400};
    }

    .titulo {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 700;
        color: ${({ variante, theme }) => variante === 'aceitar' ? theme.colors.success500 : theme.colors.primaryRed400};

        .icon-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            
            svg {
                width: 18px;
                height: 13px;
                
                path {
                    fill: ${({ variante, theme }) => variante === 'aceitar' ? 'var(--c16a34a)' : theme.colors.primaryRed400} !important;
                }
            }
        }
    }

    .subtitulo {
        color: ${({ theme }) => theme.colors.interfaceText300};
        font-size: 14px;
        line-height: 20px;
    }
`;

const CabecalhoEscolha = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
    width: 100%;

    .titulo {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 700;
        font-size: 16px;
        color: ${({ theme }) => theme.colors.interfaceText400};
        line-height: 24px;
        text-align: left;
        width: 100%;

        .icon-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            
            svg {
                width: 18px;
                height: 13px;
                
                path {
                    fill: #16a34a !important;
                }
            }
        }

        svg {
            flex-shrink: 0;
        }

        span {
            flex: 1;
        }
    }

    .subtitulo {
        color: ${({ theme }) => theme.colors.interfaceText300};
        font-size: 14px;
        line-height: 20px;
        text-align: left;
    }
`;

interface ModalFinalizarDemandaProps {
    modalAtivo: boolean;
    setModalAtivo: (ativo: boolean) => void;
    onConfirmar: (statusFinalizacao: 'Aceita' | 'Recusada', justificativa: string) => Promise<void>;
    loading?: boolean;
}

export default function ModalFinalizarDemanda({ 
    modalAtivo, 
    setModalAtivo, 
    onConfirmar,
    loading = false
}: ModalFinalizarDemandaProps) {
    const [resultado, setResultado] = useState<'' | 'Aceita' | 'Recusada'>('');
    const [justificativa, setJustificativa] = useState('');

    const handleConfirmar = async () => {
        if (!resultado) return;
        const texto = justificativa.trim();
        await onConfirmar(resultado, texto);
    };

    const handleCancelar = () => {
        setResultado('');
        setJustificativa('');
        setModalAtivo(false);
    };

    const handleVoltarParaEscolha = () => {
        setResultado('');
        setJustificativa('');
    };

    const botaoDesabilitado = loading || !justificativa.trim();
    const selecionadoAceitar = resultado === 'Aceita';
    const selecionadoRecusar = resultado === 'Recusada';

    useEffect(() => {
        if (!modalAtivo) {
            setResultado('');
            setJustificativa('');
        }
    }, [modalAtivo]);

    return (
        <ModalDefault
            id={'overlayModal'}
            onClose={handleCancelar}
            openModal={modalAtivo}
            width={resultado ? "600px" : "fit-content"}
            maxWidth={resultado ? "90vw" : "98vw"}
        >
            <ModalWrapper>
                <ModalTitle>
                    <div className="points">
                        Finalizar demanda
                    </div>
                    <div
                        className="close"
                        onClick={handleCancelar}
                    >
                        <IconClose />
                    </div>
                </ModalTitle>

                <ContentWrapper>
                    {!resultado && (
                        <>
                            <p style={{ marginBottom: '16px', color: 'var(--c666666)' }}>
                                Escolha como deseja finalizar a demanda.
                            </p>
                            <Opcoes>
                                <OpcaoCard
                                    type="button"
                                    variante="aceitar"
                                    selecionado={selecionadoAceitar}
                                    onClick={() => setResultado('Aceita')}
                                    disabled={loading}
                                >
                                    <div className="titulo">
                                        <div className="icon-wrapper">
                                            <IconAccept />
                                        </div>
                                        Aceitar
                                    </div>
                                    <div className="subtitulo">
                                        Resultado da demanda está adequado.
                                    </div>
                                </OpcaoCard>
                                <OpcaoCard
                                    type="button"
                                    variante="recusar"
                                    selecionado={selecionadoRecusar}
                                    onClick={() => setResultado('Recusada')}
                                    disabled={loading}
                                >
                                    <div className="titulo">
                                        <IconWrong /> Recusar
                                    </div>
                                    <div className="subtitulo">
                                        Resultado da demanda está inadequado.
                                    </div>
                                </OpcaoCard>
                            </Opcoes>
                        </>
                    )}

                    {resultado && (
                        <>
                            <CabecalhoEscolha>
                                <div className="titulo">
                                    {resultado === 'Aceita' ? (
                                        <div className="icon-wrapper">
                                            <IconAccept />
                                        </div>
                                    ) : (
                                        <IconWrong />
                                    )}
                                    <span>{resultado === 'Aceita' ? 'Aceitar demanda' : 'Recusar demanda'}</span>
                                </div>
                                <div className="subtitulo">
                                    {resultado === 'Aceita'
                                        ? 'Descreva o resultado para o operador.'
                                        : 'Justifique o motivo da recusa.'}
                                </div>
                            </CabecalhoEscolha>

                            <TextareaContainer>
                                <label htmlFor="mensagem-textarea">
                                    {resultado === 'Aceita' ? 'Mensagem' : 'Justificativa'}
                                </label>
                                <textarea
                                    id="mensagem-textarea"
                                    placeholder={resultado === 'Aceita' ? 'Escreva aqui sua mensagem' : 'Escreva aqui sua justificativa'}
                                    value={justificativa}
                                    onChange={(e) => setJustificativa(e.target.value)}
                                    disabled={loading}
                                />
                            </TextareaContainer>
                        </>
                    )}
                </ContentWrapper>

                {resultado && (
                    <ModalButtons>
                        <ButtonDefault
                            className="cancel"
                            onClick={handleVoltarParaEscolha}
                            disabled={loading}
                        >
                            Voltar
                        </ButtonDefault>
                        <ButtonDefault
                            className="continue"
                            onClick={handleConfirmar}
                            disabled={botaoDesabilitado}
                        >
                            {loading ? 'Processando...' : 'Confirmar'}
                        </ButtonDefault>
                    </ModalButtons>
                )}
            </ModalWrapper>
        </ModalDefault>
    );
}

